import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Pencil, ChevronUp, ChevronDown, Loader2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function CategoriesAdmin() {
  const qc = useQueryClient();
  const { data: categories = [], isLoading } = useQuery({ queryKey: ["categories"], queryFn: () => api.categories() });
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [busy, setBusy] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ["categories"] });

  const add = async () => {
    if (!newName.trim()) { toast.error("Enter a category name"); return; }
    setAdding(true);
    try { await api.addCategory(newName.trim()); toast.success("Category added"); setNewName(""); refresh(); }
    catch (e) { toast.error(e.message); } finally { setAdding(false); }
  };

  const move = async (index, dir) => {
    const j = index + dir;
    if (j < 0 || j >= categories.length) return;
    const next = [...categories];
    [next[index], next[j]] = [next[j], next[index]];
    setBusy(true);
    try { await api.reorderCategories(next); refresh(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const startEdit = (name) => { setEditing(name); setEditVal(name); };

  const saveRename = async (old) => {
    if (!editVal.trim()) { toast.error("Enter a name"); return; }
    setBusy(true);
    try { await api.renameCategory(old, editVal.trim()); toast.success("Category renamed"); setEditing(null); qc.invalidateQueries({ queryKey: ["categories"] }); qc.invalidateQueries({ queryKey: ["products"] }); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const remove = async (name) => {
    if (!window.confirm(`Delete category "${name}"? Products using it must be moved first.`)) return;
    setBusy(true);
    try { await api.deleteCategory(name); toast.success("Category deleted"); refresh(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  if (isLoading) return <p className="text-muted2">Loading…</p>;

  return (
    <div className="max-w-xl">
      <h2 className="font-display text-2xl text-ink mb-1">Categories</h2>
      <p className="text-xs text-muted2 mb-5">Add categories, rename them, and use the arrows to set the order they appear on the store home page and menu.</p>

      <div className="flex gap-2 mb-5">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="New category name"
          className="flex-1 h-11 rounded-lg border border-line px-3 text-sm focus:outline-none focus:border-brand"
          data-testid="category-new-input"
        />
        <button onClick={add} disabled={adding} className="h-11 px-4 rounded-full bg-brand text-white font-semibold text-sm flex items-center gap-1.5 disabled:opacity-60" data-testid="category-add-button">
          {adding ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />} Add
        </button>
      </div>

      <div className="space-y-2">
        {categories.map((c, i) => (
          <div key={c} className="flex items-center gap-2 rounded-xl border border-line p-2.5" data-testid={`category-row-${c}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0 || busy} className="text-muted2 hover:text-brand disabled:opacity-30" data-testid={`category-up-${c}`}><ChevronUp size={16} /></button>
              <button onClick={() => move(i, 1)} disabled={i === categories.length - 1 || busy} className="text-muted2 hover:text-brand disabled:opacity-30" data-testid={`category-down-${c}`}><ChevronDown size={16} /></button>
            </div>
            {editing === c ? (
              <>
                <input value={editVal} onChange={(e) => setEditVal(e.target.value)} className="flex-1 h-9 rounded border border-line px-2 text-sm focus:outline-none focus:border-brand" data-testid="category-rename-input" autoFocus />
                <button onClick={() => saveRename(c)} disabled={busy} className="p-2 text-ok" data-testid={`category-rename-save-${c}`}><Check size={16} /></button>
                <button onClick={() => setEditing(null)} className="p-2 text-muted2"><X size={16} /></button>
              </>
            ) : (
              <>
                <span className="flex-1 text-sm text-ink font-medium">{c}</span>
                <span className="text-xs text-muted2 mr-1">#{i + 1}</span>
                <button onClick={() => startEdit(c)} className="p-2 text-muted2 hover:text-brand" data-testid={`category-edit-${c}`}><Pencil size={15} /></button>
                <button onClick={() => remove(c)} className="p-2 text-muted2 hover:text-err" data-testid={`category-delete-${c}`}><Trash2 size={15} /></button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
